import { Controller } from '@hotwired/stimulus'

// Emits analytics events to /api/v1/events. Buffers and flushes via sendBeacon on
// pagehide / tab-hide (reliable end-of-session delivery), and immediately on the
// 100% milestone. kindValue is "story" or "chapter". Auto-registered by the glob.
export default class extends Controller {
  static values = { url: String, storyId: String, chapterId: String, kind: String }
  static targets = ['audio']

  // A non-scrollable (fits-the-viewport) chapter can't be measured by scroll, so
  // we count it as read only after the reader DWELLS this long, instead of
  // reporting 100% the instant it loads (which inflated read-completion on short
  // chapters and on immediate bounces).
  static SHORT_READ_DWELL_MS = 8000

  connect() {
    this.queue = []
    this.readMarks = new Set()
    this.listenMarks = new Set()
    this.shortReadTimer = null
    this.boundFlush = () => this.flush()
    this.boundScroll = () => this.onScroll()
    this.boundHide = () => { if (document.visibilityState === 'hidden') this.flush() }

    this.track(this.kindValue === 'chapter' ? 'chapter_view' : 'story_view')

    if (this.kindValue === 'chapter') {
      window.addEventListener('scroll', this.boundScroll, { passive: true })
      this.onScroll()
    }
    if (this.hasAudioTarget) {
      this.audioTarget.addEventListener('play', () => this.track('audio_play'), { once: true })
      this.audioTarget.addEventListener('timeupdate', () => this.onAudio())
    }
    document.addEventListener('visibilitychange', this.boundHide)
    window.addEventListener('pagehide', this.boundFlush)
  }

  disconnect() {
    window.removeEventListener('scroll', this.boundScroll)
    document.removeEventListener('visibilitychange', this.boundHide)
    window.removeEventListener('pagehide', this.boundFlush)
    // Leaving before the dwell elapses must NOT count a short chapter as read.
    if (this.shortReadTimer) clearTimeout(this.shortReadTimer)
    this.flush()
  }

  onScroll() {
    const el = document.documentElement
    const max = el.scrollHeight - el.clientHeight
    if (max <= 0) {
      // Non-scrollable: can't measure reading by scroll, so count it as read
      // only after a dwell (set once), not instantly on load.
      this.scheduleShortRead()
      return
    }
    const pct = Math.round((el.scrollTop / max) * 100)
    this.milestones('read_progress', pct, this.readMarks)
  }

  scheduleShortRead() {
    if (this.shortReadTimer || this.readMarks.has(100)) return
    this.shortReadTimer = setTimeout(() => {
      this.milestones('read_progress', 100, this.readMarks)
    }, this.constructor.SHORT_READ_DWELL_MS)
  }

  onAudio() {
    const { currentTime, duration } = this.audioTarget
    if (!duration) return
    this.milestones('listen_progress', Math.round((currentTime / duration) * 100), this.listenMarks)
  }

  milestones(name, pct, marks) {
    for (const m of [25, 50, 75, 100]) {
      if (pct >= m && !marks.has(m)) {
        marks.add(m)
        this.track(name, m)
        if (m === 100) this.flush()
      }
    }
  }

  track(name, percent = 0) {
    this.queue.push({
      name,
      story_id: this.storyIdValue,
      chapter_id: this.chapterIdValue || null,
      percent,
      occurred_at: new Date().toISOString()
    })
    if (this.queue.length >= 10) this.flush()
  }

  flush() {
    if (!this.queue.length || !navigator.sendBeacon) return
    const body = JSON.stringify({ events: this.queue })
    this.queue = []
    navigator.sendBeacon(this.urlValue, new Blob([body], { type: 'application/json' }))
  }
}
